import React, { useMemo } from 'react';
import { RevenueCalculation, PrimesData, OverseasTerritoryId, CotisationsData } from '../types';
import { formatCurrency } from '../utils/formatting';
import { calculateProgressiveTax } from '../utils/taxCalculations';
import LoadingSpinner, { SkeletonRevenue } from './ui/LoadingSpinner';
import { useSalaryCalculations } from '../hooks/useSalaryCalculations';
import PeriodToggle from './ui/PeriodToggle';
import RevenueCardsDisplay from './RevenueCardsDisplay';
import DetailedCalculationBreakdown from './DetailedCalculationBreakdown';

interface RevenueCalculatorProps {
  id?: string;
  calculation: RevenueCalculation | null;
  retrocessionAmount?: number;
  selectedGrade?: number | null;
  selectedEchelon?: number | null;
  workTimePercentage?: number;
  selectedOverseasTerritory?: OverseasTerritoryId;
  primesData?: PrimesData;
  cotisationsData?: CotisationsData;
  grades?: GradeData[];
  isLoadingCotisations?: boolean;
  isLoading?: boolean;
  selectedProfession?: Profession;
}

export default function RevenueCalculator({ 
  id,
  calculation, 
  retrocessionAmount = 0, 
  selectedGrade = null, 
  selectedEchelon = null, 
  workTimePercentage = 100,
  selectedOverseasTerritory = null,
  primesData,
  cotisationsData,
  grades = [],
  isLoadingCotisations = false,
  isLoading = false,
  selectedProfession
}: RevenueCalculatorProps) {
  const [showCalculationDetail, setShowCalculationDetail] = React.useState<boolean>(false);
  const [isAnnual, setIsAnnual] = React.useState<boolean>(false);
  const [isCalculating, setIsCalculating] = React.useState<boolean>(false);
  const [customTaxRate, setCustomTaxRate] = React.useState<number | null>(null);

  // Use salary calculations hook
  const { salaryData, isCalculating: hookIsCalculating } = useSalaryCalculations({
    selectedGrade,
    selectedEchelon,
    workTimePercentage,
    grades,
    selectedOverseasTerritory,
    primesData: primesData || { primes: [], totalMensuel: 0 },
    cotisationsData: cotisationsData || { cotisations: [], totalMensuel: 0, totalAnnuel: 0 },
    isLoading
  });

  // Sync calculating state
  React.useEffect(() => {
    setIsCalculating(hookIsCalculating);
  }, [hookIsCalculating]);

  // Calculate tax amount using progressive tax calculation
  const calculateTaxAmount = useMemo(() => {
    if (!salaryData) return 0;
    
    // Use custom tax rate if set, otherwise use progressive calculation
    if (customTaxRate !== null) {
      return (salaryData.taxableIncome * customTaxRate) / 100;
    }
    
    const taxCalculation = calculateProgressiveTax(salaryData.taxableIncome);
    return taxCalculation.tax;
  }, [salaryData, customTaxRate]);

  // Calculate net after tax
  const netAfterTaxWithCustomRate = useMemo(() => {
    if (!salaryData) return { annual: 0, monthly: 0 };
    
    const netAfterTax = salaryData.netAfterChargesAnnual - calculateTaxAmount;
    return {
      annual: netAfterTax,
      monthly: netAfterTax / 12
    };
  }, [salaryData, calculateTaxAmount]);

  // Show skeleton while loading or calculating
  if (isLoading || isCalculating || isLoadingCotisations) {
    return <SkeletonRevenue />;
  }

  if (!salaryData) {
    return (
      <div className="bg-white rounded-xl shadow-lg p-4 md:p-6">
        <div className="text-center py-8">
          <LoadingSpinner className="mb-4" text="En attente de vos informations" />
          <p className="text-gray-500">Complétez les informations pour voir le calcul de vos revenus</p>
        </div>
      </div>
    );
  }

  return (
    <div id={id} className="bg-white rounded-xl shadow-lg p-4 md:p-6">
      {isCalculating && (
        <div className="mb-4 flex justify-end">
          <LoadingSpinner size="sm" />
        </div>
      )}

      <PeriodToggle isAnnual={isAnnual} onToggle={setIsAnnual} />

      <RevenueCardsDisplay
        salaryData={salaryData}
        isAnnual={isAnnual}
        netAfterTaxWithCustomRate={netAfterTaxWithCustomRate}
        selectedOverseasTerritory={selectedOverseasTerritory}
      />

      <div className="mb-6">
        <button
          onClick={() => setShowCalculationDetail(!showCalculationDetail)}
          className="w-full flex items-center justify-between p-3 sm:p-4 bg-white border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors"
        >
          <h4 className="font-medium text-gray-800 text-sm sm:text-base">
            {showCalculationDetail ? 'Masquer le détail du calcul' : 'Afficher le détail du calcul'}
          </h4>
          <div className={`transform transition-transform ${showCalculationDetail ? 'rotate-180' : ''}`}>
            <svg className="w-5 h-5 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
            </svg>
          </div>
        </button>
        
        {showCalculationDetail && (
          <DetailedCalculationBreakdown
            salaryData={salaryData}
            isAnnual={isAnnual}
            calculateTaxAmount={calculateTaxAmount}
            netAfterTaxWithCustomRate={netAfterTaxWithCustomRate}
            selectedOverseasTerritory={selectedOverseasTerritory}
            customTaxRate={customTaxRate}
            onCustomTaxRateChange={setCustomTaxRate}
          />
        )}
        </div>
    </div>
  );
}